/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Rotate Transform Node
   Rotates image around center with optional bounding box expansion.
   ═══════════════════════════════════════════════════════════════ */

import type { NodeDefinition } from '../types';
import { GLSL_HEADER } from '../../rendering/effects/glslCommon';
import { createShaderProcessor } from '../../rendering/effects/shaderProcessor';
import { getMediaDimensions } from '../../rendering/media';

const ROTATE_FRAGMENT_SHADER = `${GLSL_HEADER}
uniform sampler2D u_image;
uniform float u_angleRad;
uniform vec2 u_srcResolution;
uniform vec2 u_dstResolution;

void main() {
  vec2 dstCoord = (v_uv - vec2(0.5)) * u_dstResolution;
  float c = cos(-u_angleRad);
  float s = sin(-u_angleRad);
  vec2 srcCoord = vec2(
    dstCoord.x * c - dstCoord.y * s,
    dstCoord.x * s + dstCoord.y * c
  );
  vec2 uv = (srcCoord / u_srcResolution) + vec2(0.5);

  if (uv.x < 0.0 || uv.x > 1.0 || uv.y < 0.0 || uv.y > 1.0) {
    fragColor = vec4(0.0);
    return;
  }

  fragColor = texture(u_image, uv);
}
`;

export const rotateNode: NodeDefinition = {
  type: 'transform.rotate',
  category: 'Transform',
  label: 'Rotate',
  description: 'Rotates an image around its center with clean anti-aliasing',
  keywords: ['rotate', 'angle', 'turn', 'orientation', 'spin'],
  targetBackend: 'webgl2',
  inputs: [
    { id: 'image', name: 'Source', type: 'image' },
  ],
  outputs: [
    { id: 'output', name: 'Rotated', type: 'image' },
  ],
  params: [
    {
      id: 'angle',
      name: 'Angle',
      type: 'angle',
      defaultValue: 0,
      min: 0,
      max: 360,
      step: 1,
      unit: '°',
      uiHint: 'angle',
    },
    {
      id: 'expand',
      name: 'Expand Bounds',
      type: 'boolean',
      defaultValue: true,
      uiHint: 'toggle',
    },
  ],
  processor: createShaderProcessor({
    fragmentShader: ROTATE_FRAGMENT_SHADER,
    calculateDimensions: (inputs, params) => {
      const srcDim = getMediaDimensions(inputs.image);
      const angleDeg = Number(params.angle) || 0;
      const expand = Boolean(params.expand ?? true);

      if (!expand) {
        return srcDim;
      }

      const rad = (angleDeg * Math.PI) / 180;
      const cos = Math.abs(Math.cos(rad));
      const sin = Math.abs(Math.sin(rad));
      const w = Math.round(srcDim.width * cos + srcDim.height * sin);
      const h = Math.round(srcDim.width * sin + srcDim.height * cos);
      return {
        width: Math.max(1, w),
        height: Math.max(1, h),
      };
    },
    getUniforms: (inputs, params, dimensions) => {
      const srcDim = getMediaDimensions(inputs.image);
      const angleDeg = Number(params.angle) || 0;
      const rad = (angleDeg * Math.PI) / 180;

      return {
        u_image: inputs.image as unknown as import('../../rendering/types').TextureHandle,
        u_angleRad: rad,
        u_srcResolution: [srcDim.width, srcDim.height],
        u_dstResolution: [dimensions.width, dimensions.height],
      };
    },
  }),
};
